import {
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
  MouseEvent,
} from "react";

import { formatHours } from "../utils";
import { validateTimeInput } from "../utils";

export interface IInputHours {
  value?: string | number;
  withLeadingZero?: boolean;
  onSave?: (e: Event) => void;
  validation?: (...args) => boolean;
  allowEmptyValue?: boolean;
  onCancel?: (e: Event) => void;
  minuteIncrement?: number;
  incrementOnlySelected?: boolean;
  onEnterKeyPress?: (val: string) => void;
  onChange?: (val: string) => void;
  onClick?: (event: MouseEvent<HTMLInputElement>) => void;
}

export const useInputHours = (
  {
    value,
    withLeadingZero,
    onSave,
    validation = validateTimeInput,
    allowEmptyValue,
    onCancel,
    minuteIncrement = 1,
    incrementOnlySelected,
    onEnterKeyPress,
    onChange,
    onClick,
  }: IInputHours,
  inputRef: RefObject<HTMLInputElement | null>
) => {
  const [currentValue, setCurrentValue] = useState(() =>
    formatHours(value, withLeadingZero)
  );
  const [prevValue, setPrevValue] = useState(() =>
    formatHours(value, withLeadingZero)
  );

  const escapeRef = useRef(false);

  const handleBlur = useCallback(
    (e) => {
      if (escapeRef.current) {
        setCurrentValue(prevValue);
      } else {
        if (e.target.value.trim().length > 0 && prevValue !== e.target.value) {
          const _value = formatHours(e.target.value, withLeadingZero);
          setPrevValue(_value);
          setCurrentValue(_value);
          typeof onSave === "function" && onSave(e);
        } else {
          if (!allowEmptyValue) {
            setCurrentValue(prevValue);
            typeof onCancel === "function" && onCancel(e);
          } else {
            if (typeof onSave === "function" && prevValue !== e.target.value) {
              onSave(e);
            } else {
              typeof onCancel === "function" && onCancel(e);
            }
          }
        }
      }
    },
    [allowEmptyValue, onCancel, onSave, prevValue, withLeadingZero]
  );

  const handleIncrementDecrement = useCallback(
    (increment: boolean) => {
      if (inputRef.current) {
        const selectionStart = inputRef.current.selectionStart;
        if (selectionStart !== null) {
          const dotsIndex = currentValue.indexOf(":");
          const [hours, minutes] = currentValue.split(":").map(Number);
          let newHours = hours;
          let newMinutes = minutes;

          if (selectionStart < dotsIndex) {
            if (increment) {
              newHours += 1;
            } else {
              newHours -= 1;
              if (newHours < 0) newHours = 0;
            }
          } else if (selectionStart > dotsIndex) {
            if (increment) {
              newMinutes += minuteIncrement;
              if (newMinutes > 59) {
                newMinutes = 0;
                if (!incrementOnlySelected) newHours += 1;
              }
            } else {
              if (newMinutes >= minuteIncrement || newMinutes === 0) {
                newMinutes -= minuteIncrement;
                if (newMinutes < 0 && newHours > 0) {
                  newMinutes = 60 - minuteIncrement;
                  if (!incrementOnlySelected) newHours -= 1;
                }
                if (newHours < 0) {
                  newHours = 0;
                }
              } else {
                newMinutes = 0;
              }
            }
          }

          const newMinutesString =
            newMinutes < 10 ? `0${newMinutes}` : newMinutes;
          const newHoursString =
            withLeadingZero && newHours < 10 ? `0${newHours}` : newHours;
          const newValue = `${newHoursString}:${newMinutesString}`;

          if (validation(newValue, withLeadingZero)) {
            setCurrentValue(newValue);

            if (onChange) onChange(newValue);

            requestAnimationFrame(() => {
              const newDotsIndex = newValue.indexOf(":");
              const isHoursSelected = selectionStart < newDotsIndex;
              const newSelectionStart = isHoursSelected ? 0 : newDotsIndex + 1;
              const selectionEnd = isHoursSelected
                ? newDotsIndex
                : newValue.length;
              inputRef.current?.setSelectionRange(
                newSelectionStart,
                selectionEnd
              );
            });
          }
        }
      }
    },
    [
      currentValue,
      incrementOnlySelected,
      inputRef,
      minuteIncrement,
      onChange,
      validation,
      withLeadingZero,
    ]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.target.blur();
        if (typeof onEnterKeyPress === "function")
          onEnterKeyPress(e.target.value);
      }
      if (e.key === "ArrowLeft") {
        return;
      }
      if (e.key === "ArrowRight") {
        return;
      }
      if (e.key === "Escape") {
        escapeRef.current = true;
        e.target.blur();
        typeof onCancel === "function" && onCancel(e);
        escapeRef.current = false;
      }
      if (e.key === "Backspace") {
        return;
      }
      if (e.key === "Delete") {
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "a") {
        inputRef.current?.select();
        return;
      }
      const input = e.target;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const currentValue = input.value;
      if (e.key === "Tab") {
        if (start !== end) {
          if (e.shiftKey) {
            const newDotsIndex = currentValue.indexOf(":");
            const isMinutesSelected = start > newDotsIndex;
            if (isMinutesSelected) {
              e.preventDefault();
              inputRef.current?.setSelectionRange(0, newDotsIndex);
            }
          } else {
            const newDotsIndex = currentValue.indexOf(":");
            const isHoursSelected = start < newDotsIndex;
            if (isHoursSelected) {
              e.preventDefault();
              inputRef.current?.setSelectionRange(
                newDotsIndex + 1,
                currentValue.length
              );
            }
          }
        }
        return;
      }

      if (start !== end) {
        if (e.key === "ArrowUp") {
          handleIncrementDecrement(true);
          return;
        }
        if (e.key === "ArrowDown") {
          handleIncrementDecrement(false);
          return;
        }
        const newValue =
          currentValue.substring(0, start) +
          e.key +
          currentValue.substring(end);
        if (!validation(newValue, withLeadingZero)) {
          e.preventDefault();
          return;
        }
      } else {
        const newValue =
          currentValue.substring(0, start) +
          e.key +
          currentValue.substring(end);

        if (!validation(newValue, withLeadingZero)) {
          e.preventDefault();
          return;
        }
      }
    },
    [
      handleIncrementDecrement,
      inputRef,
      onCancel,
      onEnterKeyPress,
      validation,
      withLeadingZero,
    ]
  );

  const handleChange = useCallback(
    (e) => {
      setCurrentValue(e.target.value);
      if (onChange) onChange(e.target.value);
    },
    [onChange]
  );

  const handleClick = useCallback(
    (e) => {
      const selectionStart = inputRef.current?.selectionStart;
      if (
        inputRef.current &&
        currentValue &&
        currentValue.length > 0 &&
        typeof selectionStart === "number"
      ) {
        const dotsIndex = currentValue.indexOf(":");
        if (selectionStart < dotsIndex) {
          inputRef.current?.setSelectionRange(0, dotsIndex);
        } else if (selectionStart >= dotsIndex) {
          inputRef.current?.setSelectionRange(
            dotsIndex + 1,
            currentValue.length
          );
        }
      }

      if (typeof onClick === "function") {
        onClick(e);
      }
    },
    [currentValue, inputRef, onClick]
  );

  const handleDoubleClick = useCallback(() => {
    if (inputRef.current) {
      inputRef.current?.select();
    }
  }, [inputRef]);

  const inputProps = useMemo(() => {
    return {
      value: currentValue,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      onChange: handleChange,
      onClick: handleClick,
      onDoubleClick: handleDoubleClick,
    };
  }, [
    currentValue,
    handleBlur,
    handleChange,
    handleClick,
    handleDoubleClick,
    handleKeyDown,
  ]);

  return {
    inputProps,
    setCurrentValue,
    setPrevValue,
  };
};
